import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../i18n';
import '../css/Login.css';
import { ExternalId } from './authConfig';
import Logo from '../components/logos/Logo';

import { Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuthInstance } from '../helpers/Auth';
import { ActiveNavItemContext } from '../components/rightPanel/RightPanelContext';
import {
  assistantsQueryParams,
  chatPageNavigation,
  disclaimerPageNavigation,
  welcomePageNavigation,
} from '../components/staticComponents/StaticHtmlGenerator';
import baseUrl from '../data/api/baseUrl.json';
import { SubPanelAssistants } from '../components/rightPanel/subpanel/SubPanelEnums';

export default function Login() {
  const { t } = useTranslation();
  const [externalId, setExternalId] = useState(ExternalId.tenantName);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { setActiveNavItem } = useContext(ActiveNavItemContext);
  const currenturl: string = baseUrl.baseUrl[0].name;

  const handleNavigation = useCallback(
    async (authInstance: any) => {
      const navigateDisclaimer = await authInstance.shouldShowDisclaimer();
      const queryParamExists = (assistantsQueryParams as Array<keyof typeof SubPanelAssistants>).some((key) =>
        JSON.parse(sessionStorage.getItem(SubPanelAssistants[key]) as string)
      );

      if (navigateDisclaimer) {
        navigate(disclaimerPageNavigation);
        await authInstance.acknowledgeDisclaimer();
      } else if (queryParamExists) {
        navigate(chatPageNavigation);
      } else {
        navigate(welcomePageNavigation);
        setActiveNavItem(t('welcomepage.Home'));
      }
    },
    [navigate, setActiveNavItem, t]
  );

  // Check initial auth status
  useEffect(() => {
    checkInitialAuth();
  }, [handleNavigation]);

  const checkInitialAuth = async () => {
    try {
      const authInstance = await getAuthInstance();
      const isLoggedIn = authInstance.isLoggedIn();

      if (isLoggedIn) {
        await handleNavigation(authInstance);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  // Handle login click with error boundaries
  const handleLoginClick = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      const authInstance = await getAuthInstance();
      setExternalId(authInstance.getExternalId());
      await authInstance.login();

      if (authInstance.isLoggedIn()) {
        await handleNavigation(authInstance);
      }
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTermsorPrivacyClick = (title: string) => {
    const page =
      title === t('termsAndConditions.title1')
        ? window.open(currenturl + 'termsAndPolicies/terms_and_conditions.html')
        : window.open(currenturl + 'termsAndPolicies/privacy_policy.html');

    if (page) {
      page.onload = () => {
        page.document.title = title;
      };
    }
  };

  return (
    <div className="login">
      <div className="login-outer">
        <div className="d-flex login-style">
          <div className="login-l">
            <Logo externalId={externalId} hideExternalIdText={true} />
            <div className="my-15">
              <Button
                variant="outlined"
                onClick={handleLoginClick}
                disabled={loading}
                sx={{ color: 'black', borderColor: 'black', '&:hover': { borderColor: 'black !important' } }}
              >
                {loading ? <CircularProgress size={24} /> : t('loginButtonTitle')}
              </Button>
            </div>
            <div className="my-15">{t('or')}</div>
            <div style={{ lineHeight: '25px' }}>
              {t('welcomeMessage.forAccess')}
              <a
                style={{ display: 'block' }}
                href={`mailto:${t('welcomeMessage.supportMail')}`}
                target="_blank"
                rel="noreferrer"
              >
                {t('welcomeMessage.supportMail')}
              </a>
            </div>
          </div>
          <div className="login-r">
            <h2>
              <div>{t('welcomeMessage.part1')}</div>
              <div>{t('welcomeMessage.part2')}</div>
              <br />
              <div>{t('welcomeMessage.part3')}</div>
            </h2>
            <p>{t('welcomeDescription.part1')}</p>
            <p>{t('welcomeDescription.part2')}</p>
            <p>{t('welcomeDescription.part3')}</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.25rem', color: '#00000080' }}>
          <p>
            <span>{t('termsAndConditions.part1')}</span>
            <a
              onClick={() => handleTermsorPrivacyClick(t('termsAndConditions.title1'))}
              style={{ color: '#256AFF', textDecoration: 'none', cursor: 'pointer' }}
            >
              {t('termsAndConditions.part2')}
            </a>
            <span>{t('termsAndConditions.part3')}</span>
            <a
              onClick={() => handleTermsorPrivacyClick(t('termsAndConditions.title2'))}
              style={{ color: '#256AFF', textDecoration: 'none', cursor: 'pointer' }}
            >
              {t('termsAndConditions.part4')}
            </a>
            <span>{t('termsAndConditions.part5')}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
